/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useInfiniteQuery, useQueryClient} from 'react-query';
import {getCVSPaginatedAssets} from '@ion/core/src/services/ArcGIS';
import {CVSAssetType, GenericObject} from '@ion/core/src/types/entities';
import {AssetQueryParams} from '@ion/core/src/types/services';
import {createKey, QueryKey} from '../config/QueryKeys';
import {
  createAssetsMap,
  createQueryResult,
  getAlignedRoutes,
  getAssetsMapPrevious,
  getLastTime
} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseLoadAssetUpdatesV2Results = UseDefaultQueryResult & {
  results: CVSAssetType[];
  refresh: Function;
  fetchNextPage: Function;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
};

export interface UseLoadAssetUpdatesV2 {
  currentAssets?: CVSAssetType[];
  parentBaseKey: QueryKey;
  baseKey: QueryKey;
  enabled?: boolean;
  rowsPerPage?: number;
  globalGroupId?: number;
  queryParams?: AssetQueryParams;
  refetchInterval?: number;
  propertyTime?: string;
  // `false` skips react-query's deep comparison of each refetch with the
  // previous data - each refetch is new deltas anyway, so the comparison over
  // thousands of rows only costs time. `true` (the default) keeps it ...
  structuralSharing?: boolean;
  isSmoothAssetMovementActive?: boolean;
  includeSciDevice?: boolean;
  fnCallback?: Function;
};

const getDbWriteTimeFilter = (time, ops = 'greater than') => ([{
  field : 'dbWriteTime',
  type  : 'datetime',
  value : new Date(time).toISOString(),
  ops
}]);

/**
 * Groups an array of assets by their assetId.
 * 
 * Each assetId in the resulting object contains two arrays:
 *   - nodes: holds all assets with the given assetId
 *   - routes: holds the same assets (duplicate of nodes)
 *
 * @param {Array<Object>} assets - Array of asset objects, each containing an assetId property.
 * @returns {Object} - An object where keys are assetIds and values are objects with `nodes` and `routes` arrays.
 */
const groupAssetsById = assets => {
  const result = {};

  assets?.forEach(asset => {
    const {assetId} = asset;
    if (!result[assetId]) {
      result[assetId] = {nodes: [], routes: []};
    }

    // push the same data to both nodes and routes
    result[assetId].nodes.push(asset);
    result[assetId].routes.push(asset);
  });

  return result;
};

/**
 * The useLoadAssetUpdatesV2 hook provides the Query for referencing the latest Assets data.
 *
 * ```typescript
 *
 * import {useLoadAssetUpdatesV2} from '@ion/query';
 *
 *  ...
 *
 *  const {results} = useLoadAssetUpdatesV2();
 *
 * ```
 * @param   currentAssets List of assets currently displayed on the map.
 * @param   parentBaseKey a constant passed in to identify the dependency parent key.
 * @param   baseKey a constant passed in to identify the dependency key.
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to allow for updates to occur only at the user's discretion.
 * @param   queryParams is built through Report filters to be used in the CVS request.
 * @param   globalGroupId used in the CVS request.
 * @returns UseAssetResult containing the query results and the current Tracking Asset.
 */
export const useLoadAssetUpdatesV2 = ({
  parentBaseKey,
  baseKey,
  queryParams,
  globalGroupId,
  refetchInterval,
  fnCallback,
  currentAssets = [],
  propertyTime = 'dbWriteTime',
  rowsPerPage = 10000,
  enabled = false,
  includeSciDevice = true,
  isSmoothAssetMovementActive = false,
  structuralSharing = true
}: UseLoadAssetUpdatesV2): UseLoadAssetUpdatesV2Results => {
  const lastTimeRef               = useRef<number | null>(null);
  const isSmoothMovementActiveRef = useRef(isSmoothAssetMovementActive);
  const [lastTime, setLastTime]   = useState<number | null>(null);
  const queryClient               = useQueryClient();

  const parentkeys = useMemo(() => (
    createKey(parentBaseKey, globalGroupId, queryParams)
  ), [parentBaseKey, globalGroupId, queryParams]);
    
  const cachedData = queryClient.getQueryData(parentkeys) as GenericObject;

  useEffect(() => {
    setLastTime(null);
  }, [cachedData, globalGroupId]);

  useEffect(() => {
    isSmoothMovementActiveRef.current = isSmoothAssetMovementActive;
  }, [isSmoothAssetMovementActive]);

  useEffect(() => {
    if (enabled) {
      lastTimeRef.current = lastTime || getLastTime(cachedData?.pages, propertyTime);
    }
  }, [cachedData, propertyTime, lastTime, enabled]);

  const keys = useMemo(() => (
    createKey(baseKey, globalGroupId, queryParams)
  ), [baseKey, globalGroupId, queryParams]);

  const fetchAssets = useCallback(async({pageParam = 0}) => {
    if (!lastTimeRef.current) return {assets: [], assetRoutes: {}};

    const {params}         = queryParams || {};
    const startRow         = pageParam ? ((pageParam - 1) * rowsPerPage) : 0;
    const queryAssetParams = {
      ...params,
      includeSciDevice,
      startRow,
      pageSize : rowsPerPage,
      filter   : getDbWriteTimeFilter(lastTimeRef.current)
    };

    const assets    = await getCVSPaginatedAssets(queryAssetParams, true);
    let assetRoutes = {};

    if (isSmoothMovementActiveRef.current) {
      const {assetsInExtent, assetsOutOfExtent} = fnCallback?.(assets);
      if (assetsInExtent?.length) {
        // only the fleet records that can pair with these assets are passed
        // on - the map is the same, without deep-cloning the whole fleet ...
        const previous = getAssetsMapPrevious(assetsInExtent, currentAssets);
        assetRoutes    = await getAlignedRoutes(createAssetsMap(assetsInExtent, previous), {}, true);
      }
      const assetsOutOfExtentGrouped = groupAssetsById(assetsOutOfExtent);
      assetRoutes = {...assetRoutes, ...assetsOutOfExtentGrouped};
    }

    const latestAVLTime = getLastTime(assets as any, propertyTime);
    if (latestAVLTime && latestAVLTime > lastTimeRef.current) {
      setLastTime(latestAVLTime);
    }
    return {assets, assetRoutes};
  }, [queryParams, currentAssets, rowsPerPage, propertyTime, fnCallback, includeSciDevice]);

  const query = useInfiniteQuery({
    queryKey         : keys,
    queryFn          : fetchAssets,
    getNextPageParam : (lastPage, pages) => {
      if (!lastPage || lastPage.assets.length < rowsPerPage) return undefined;
      return pages.length + 1;
    },
    enabled,
    refetchInterval,
    structuralSharing
  } as any);
    
  const {
    data,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = query;
    
  if (enabled && hasNextPage && !isFetchingNextPage) {
    fetchNextPage();
  }
            
  return createQueryResult(query, {
    results : data || [],
    refresh : refetch,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    enabled
  }) as UseLoadAssetUpdatesV2Results;
};
